import { z } from 'zod';

export const PlayerSchema = z.object({
  playerId: z.string(),
  name: z.string(),
  role: z.string(),
  cannotLoseLine: z.string(),
  constraints: z.array(z.string()).default([]),
  leverage: z.array(z.string()).default([]),
  informationSet: z.array(z.string()).default([]),
  rationalNextMove: z.string(),
  sourceIds: z.array(z.string()).min(1),
});

export type Player = z.infer<typeof PlayerSchema>;

export const ScenarioSchema = z.object({
  scenarioId: z.string(),
  eventId: z.string(),
  name: z.string(),
  description: z.string(),
  probability: z.number().min(0).max(1),
  priorProbability: z.number().min(0).max(1).optional(),
  isBearCase: z.boolean().default(false),
  marketTransmission: z.array(z.object({
    channel: z.string(),
    direction: z.enum(['positive', 'negative', 'mixed', 'uncertain']),
    magnitude: z.enum(['low', 'medium', 'high']),
    glossaryTermId: z.string(),
  })),
  sourceIds: z.array(z.string()).min(1),
});

export type Scenario = z.infer<typeof ScenarioSchema>;

export const PlayerMapSchema = z.object({
  playerMapId: z.string(),
  eventId: z.string(),
  principals: z.array(PlayerSchema).min(3),
  principalCountJustification: z.string().optional(),
  scenarios: z.array(ScenarioSchema).default([]),
  powerShiftSummary: z.array(z.string()).max(3),
  knownAt: z.string(),
  sourceIds: z.array(z.string()).min(1),
}).superRefine((map, ctx) => {
  if (map.principals.length > 7 && !map.principalCountJustification?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Player maps with more than 7 principals require principalCountJustification',
      path: ['principalCountJustification'],
    });
  }
});

export type PlayerMap = z.infer<typeof PlayerMapSchema>;

export const EventSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  summary: z.string(),
  region: z.string().optional(),
  occurredAt: z.string(),
  knownAt: z.string(),
  status: z.enum(['emerging', 'active', 'resolved', 'superseded']),
  sourceIds: z.array(z.string()).min(1),
  classification: z.enum(['FACT', 'INFERENCE', 'OPINION', 'FORECAST']),
});

export type Event = z.infer<typeof EventSchema>;
